import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import useSWR from "swr";
import { swrKey, factoringFetcher } from "components/getFactoringApi";
import FactoringComponent from "./FactoringComponent.tsx";
import FactoringEntryComponent from "./FactoringEntryComponent.tsx";
import { useDeleteFactoring } from "components/deleteFactoring";

export default function FactoringDetail() {
  const { factoringId } = useParams<{ factoringId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const { data, isLoading, error, mutate } = useSWR(
    [swrKey, factoringId],
    factoringFetcher,
  );

  const { deleteFactoring, isDeleting, error: deleteError } = useDeleteFactoring();

  // Handle refresh
  const refreshFactoring = async () => {
    await mutate();
  };

  // Handle delete button click
  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteConfirmText === "削除" && factoringId && data?.userId) {
      const { success } = await deleteFactoring(data.userId, factoringId);
      if (success) {
        setOpenDeleteDialog(false);
        navigate("/factoring");
      }
    }
  };

  // Handle back to list
  const handleBackToList = () => {
    // Get the returnPage parameter from the URL
    const returnPage = searchParams.get("returnPage");
    if (returnPage) {
      // Navigate back to the list with the page parameter
      navigate(`/factoring?page=${returnPage}`);
    } else {
      // If no returnPage parameter, just navigate back to the list
      navigate("/factoring");
    }
  };

  // Handle image upload
  const handleImageUpload = () => {
    // Get the returnPage parameter from the URL
    const returnPage = searchParams.get("returnPage");
    if (returnPage) {
      // Navigate to images with the returnPage parameter
      navigate(
        `/factoring/${factoringId}/details/images?returnPage=${returnPage}`,
      );
    } else {
      // If no returnPage parameter, just navigate to images
      navigate(`/factoring/${factoringId}/details/images`);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            ファクタリング申込詳細
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="60vh"
          >
            <CircularProgress size={60} />
          </Box>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ p: 3, color: "error.main" }}>
          <Typography>
            エラーが発生しました: {error.message || "データの取得に失敗しました"}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ p: 3, color: "error.main" }}>
          <Typography>
            エラーが発生しました: データの取得に失敗しました
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Main Content */}
      <FactoringComponent factoring={data} refresh={refreshFactoring} />

      {/* Entries Content */}
      <FactoringEntryComponent factoring={data} refresh={refreshFactoring} />

      {/* Images Section */}
      <Paper sx={{ p: 3, mb: 3, border: "none", elevation: 0 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          画像
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImageUpload}
          >
            画像を確認する
          </Button>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button variant="outlined" onClick={handleBackToList}>
          申込一覧へ戻る
        </Button>
        <Button
          variant="contained"
          color="error"
          sx={{ mr: 2 }}
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? "削除中..." : "申込を削除"}
        </Button>
      </Box>

      {deleteError &&
        <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
          <Typography variant="h6" color="error">
            削除エラーが発生しました
          </Typography>
          <Typography variant="body2">
            {deleteError.message || "ファクタリングの削除中に問題が発生しました。"}
          </Typography>
        </Box>
      }

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">申込を削除します。</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {data.applicationNumber}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            削除する場合は、下記に削除と記入してください。
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            color="primary"
            disabled={isDeleting}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            disabled={deleteConfirmText !== "削除" || isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : "削除"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
